import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const JobOfferForm = ({
  jobOffer,
  onSubmit,
  onCancel,
  departments = [],
  showActions = true,
  onFormSubmit,
  formRef,
}) => {
  const [formData, setFormData] = useState({
    titre: "",
    description: "",
    departement_id: "",
    type_contrat: "",
    salaire_min: "",
    salaire_max: "",
    lieu_travail: "",
    experience_requise: "",
    competences_requises: "",
    nombre_postes: "1",
    date_fin: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const formDataRef = useRef(formData);

  // Update ref whenever formData changes
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Expose loading state and submit handler to parent
  React.useEffect(() => {
    if (onFormSubmit) {
      onFormSubmit({ loading, handleSubmit });
    }
  }, [loading, onFormSubmit]);

  useEffect(() => {
    if (jobOffer) {
      setFormData({
        titre: jobOffer.titre || "",
        description: jobOffer.description || "",
        departement_id: jobOffer.departement_id || "",
        type_contrat: jobOffer.type_contrat || "",
        salaire_min: jobOffer.salaire_min
          ? jobOffer.salaire_min.toString()
          : "",
        salaire_max: jobOffer.salaire_max
          ? jobOffer.salaire_max.toString()
          : "",
        lieu_travail: jobOffer.lieu_travail || "",
        experience_requise: jobOffer.experience_requise || "",
        competences_requises: jobOffer.competences_requises || "",
        nombre_postes: jobOffer.nombre_postes
          ? jobOffer.nombre_postes.toString()
          : "1",
        date_fin: jobOffer.date_fin
          ? new Date(jobOffer.date_fin).toISOString().split("T")[0]
          : "",
      });
    }
  }, [jobOffer]);

  const handleChange = (field, value) => {
    console.log(`Updating field ${field} with value:`, value);
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };
      console.log("New form data:", newData);
      return newData;
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const currentFormData = formDataRef.current;

    console.log("Validating form with data:", currentFormData);

    if (!currentFormData.titre || !currentFormData.titre.trim()) {
      newErrors.titre = "Le titre est requis";
    }

    if (!currentFormData.departement_id) {
      newErrors.departement_id = "Le département est requis";
    }

    if (!currentFormData.type_contrat) {
      newErrors.type_contrat = "Le type de contrat est requis";
    }

    if (currentFormData.salaire_min && currentFormData.salaire_max) {
      const min = parseFloat(currentFormData.salaire_min);
      const max = parseFloat(currentFormData.salaire_max);
      if (min > max) {
        newErrors.salaire_max =
          "Le salaire maximum doit être supérieur au minimum";
      }
    }

    if (
      currentFormData.nombre_postes &&
      parseInt(currentFormData.nombre_postes) < 1
    ) {
      newErrors.nombre_postes = "Le nombre de postes doit être au moins 1";
    }

    if (currentFormData.date_fin) {
      const finDate = new Date(currentFormData.date_fin);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (finDate < today) {
        newErrors.date_fin = "La date de fin doit être dans le futur";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const currentFormData = formDataRef.current;
    console.log("Form submitted with data:", currentFormData);

    if (!validateForm()) {
      console.log("Form validation failed");
      return;
    }

    setLoading(true);

    try {
      const submitData = { ...currentFormData };

      // Convert numeric fields
      if (submitData.salaire_min) {
        submitData.salaire_min = parseFloat(submitData.salaire_min);
      }
      if (submitData.salaire_max) {
        submitData.salaire_max = parseFloat(submitData.salaire_max);
      }
      if (submitData.nombre_postes) {
        submitData.nombre_postes = parseInt(submitData.nombre_postes);
      }

      // Remove empty fields
      Object.keys(submitData).forEach((key) => {
        if (submitData[key] === "") {
          delete submitData[key];
        }
      });

      await onSubmit(submitData);
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setLoading(false);
    }
  };

  const contractTypes = [
    { value: "cdi", label: "CDI" },
    { value: "cdd", label: "CDD" },
    { value: "stage", label: "Stage" },
    { value: "interim", label: "Intérim" },
    { value: "freelance", label: "Freelance" },
    { value: "apprentissage", label: "Apprentissage" },
  ];

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Titre */}
        <div className="md:col-span-2">
          <Label htmlFor="titre">Titre du poste *</Label>
          <Input
            id="titre"
            value={formData.titre}
            onChange={(e) => handleChange("titre", e.target.value)}
            placeholder="Ex: Développeur Full Stack Senior"
            className={errors.titre ? "border-red-500" : ""}
          />
          {errors.titre && (
            <p className="text-sm text-red-500 mt-1">{errors.titre}</p>
          )}
        </div>

        {/* Département */}
        <div>
          <Label htmlFor="departement">Département *</Label>
          <Select
            value={formData.departement_id}
            onValueChange={(value) => handleChange("departement_id", value)}
          >
            <SelectTrigger
              className={errors.departement_id ? "border-red-500" : ""}
            >
              <SelectValue placeholder="Sélectionner un département" />
            </SelectTrigger>
            <SelectContent>
              {Array.isArray(departments) &&
                departments.map((dept) => (
                  <SelectItem key={dept.id} value={dept.id}>
                    {dept.nom}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          {errors.departement_id && (
            <p className="text-sm text-red-500 mt-1">{errors.departement_id}</p>
          )}
        </div>

        {/* Type de contrat */}
        <div>
          <Label htmlFor="type_contrat">Type de contrat *</Label>
          <Select
            value={formData.type_contrat}
            onValueChange={(value) => handleChange("type_contrat", value)}
          >
            <SelectTrigger
              className={errors.type_contrat ? "border-red-500" : ""}
            >
              <SelectValue placeholder="Sélectionner un type" />
            </SelectTrigger>
            <SelectContent>
              {contractTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.type_contrat && (
            <p className="text-sm text-red-500 mt-1">{errors.type_contrat}</p>
          )}
        </div>

        {/* Salaire minimum */}
        <div>
          <Label htmlFor="salaire_min">Salaire minimum (MAD)</Label>
          <Input
            id="salaire_min"
            type="number"
            value={formData.salaire_min}
            onChange={(e) => handleChange("salaire_min", e.target.value)}
            placeholder="Ex: 8000"
            min="0"
            step="100"
          />
        </div>

        {/* Salaire maximum */}
        <div>
          <Label htmlFor="salaire_max">Salaire maximum (MAD)</Label>
          <Input
            id="salaire_max"
            type="number"
            value={formData.salaire_max}
            onChange={(e) => handleChange("salaire_max", e.target.value)}
            placeholder="Ex: 12000"
            min="0"
            step="100"
            className={errors.salaire_max ? "border-red-500" : ""}
          />
          {errors.salaire_max && (
            <p className="text-sm text-red-500 mt-1">{errors.salaire_max}</p>
          )}
        </div>

        {/* Lieu de travail */}
        <div>
          <Label htmlFor="lieu_travail">Lieu de travail</Label>
          <Input
            id="lieu_travail"
            value={formData.lieu_travail}
            onChange={(e) => handleChange("lieu_travail", e.target.value)}
            placeholder="Ex: Casablanca, Hybride, À distance"
          />
        </div>

        {/* Nombre de postes */}
        <div>
          <Label htmlFor="nombre_postes">Nombre de postes</Label>
          <Input
            id="nombre_postes"
            type="number"
            value={formData.nombre_postes}
            onChange={(e) => handleChange("nombre_postes", e.target.value)}
            min="1"
            className={errors.nombre_postes ? "border-red-500" : ""}
          />
          {errors.nombre_postes && (
            <p className="text-sm text-red-500 mt-1">{errors.nombre_postes}</p>
          )}
        </div>

        {/* Date de fin */}
        <div>
          <Label htmlFor="date_fin">Date limite de candidature</Label>
          <Input
            id="date_fin"
            type="date"
            value={formData.date_fin}
            onChange={(e) => handleChange("date_fin", e.target.value)}
            min={new Date().toISOString().split("T")[0]}
            className={errors.date_fin ? "border-red-500" : ""}
          />
          {errors.date_fin && (
            <p className="text-sm text-red-500 mt-1">{errors.date_fin}</p>
          )}
        </div>
      </div>

      <Separator />

      {/* Description */}
      <div>
        <Label htmlFor="description">Description du poste</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => handleChange("description", e.target.value)}
          placeholder="Décrivez les responsabilités, missions et objectifs du poste..."
          rows={4}
        />
      </div>

      {/* Expérience requise */}
      <div>
        <Label htmlFor="experience_requise">Expérience requise</Label>
        <Textarea
          id="experience_requise"
          value={formData.experience_requise}
          onChange={(e) => handleChange("experience_requise", e.target.value)}
          placeholder="Ex: 3 à 5 ans d'expérience en développement web, maîtrise de React et Node.js..."
          rows={3}
        />
      </div>

      {/* Compétences requises */}
      <div>
        <Label htmlFor="competences_requises">
          Compétences et qualifications
        </Label>
        <Textarea
          id="competences_requises"
          value={formData.competences_requises}
          onChange={(e) => handleChange("competences_requises", e.target.value)}
          placeholder="Ex: JavaScript, React, Node.js, SQL, Git, Anglais courant..."
          rows={3}
        />
      </div>

      {/* Actions - only show if showActions is true */}
      {showActions && (
        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Annuler
          </Button>
          <Button type="submit" disabled={loading}>
            {loading
              ? "Enregistrement..."
              : jobOffer
              ? "Mettre à jour"
              : "Créer l'offre"}
          </Button>
        </div>
      )}
    </form>
  );
};

export default JobOfferForm;
